import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrainCircuit, Loader2, Save, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MODE_LABELS = {
  FULL_AI_MODE: "Full AI (premium)",
  CACHE_FIRST_MODE: "Cache First (recommended)",
  CACHE_ONLY_MODE: "Cache Only (lowest cost)",
  DISABLED_MODE: "Disabled",
};

const TOGGLES = [
  ["allow_openai_fallback", "Allow OpenAI fallback", "Generate a fresh answer with OpenAI when no cache match exists."],
  ["allow_sarvam_tts", "Allow Sarvam TTS", "Use Sarvam streaming voice; falls back to OpenAI voice when off."],
  ["allow_sarvam_stt", "Allow voice input (STT)", "Let students ask by speaking. Off = type only."],
  ["auto_approve", "Auto-approve new AI answers", "Newly generated answers become reusable immediately."],
  ["send_to_review", "Send unanswered to teacher review", "Queue questions with no answer for a teacher to answer."],
  ["teacher_approved_global", "Teacher answers reusable school-wide", "Approved teacher Q&A can be reused across lessons of the subject."],
  ["reuse_audio", "Reuse cached audio across students", "Store & replay generated audio to save TTS cost."],
];

export default function DevAdminAiTutor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // override builder
  const [classMeta, setClassMeta] = useState({ classes: [], assignments: [] });
  const [ovLevel, setOvLevel] = useState("class");
  const [ovClass, setOvClass] = useState("");
  const [ovSection, setOvSection] = useState("");
  const [ovStudentId, setOvStudentId] = useState("");
  const [ovStudentName, setOvStudentName] = useState("");
  const [ovMode, setOvMode] = useState("CACHE_FIRST_MODE");
  const [students, setStudents] = useState([]);
  const [addingOv, setAddingOv] = useState(false);

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-tutor/config");
      setCfg(data.global);
      setOverrides(data.overrides || []);
      setModes(data.modes || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load AI Tutor config");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/meta/classes").then(({ data }) => setClassMeta(data)).catch(() => {}); }, []);

  const sectionOptions = useMemo(() => {
    const secs = (classMeta.assignments || []).filter((a) => a.class_name === ovClass).map((a) => a.section);
    return [...new Set(secs)].sort();
  }, [classMeta, ovClass]);

  useEffect(() => {
    if (ovLevel === "student" && ovClass && ovSection) {
      api.get("/students/list", { params: { class_name: ovClass, section: ovSection } })
        .then(({ data }) => setStudents(data || [])).catch(() => setStudents([]));
    }
  }, [ovLevel, ovClass, ovSection]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ai-tutor/config", cfg);
      setCfg(data.global);
      toast.success("AI Tutor settings saved & applied");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const addOverride = async () => {
    let key = "";
    let label = "";
    if (ovLevel === "class") { if (!ovClass) return toast.error("Pick a class"); key = ovClass; label = `Class ${ovClass}`; }
    if (ovLevel === "section") { if (!ovClass || !ovSection) return toast.error("Pick class & section"); key = `${ovClass}:${ovSection}`; label = `Class ${ovClass}-${ovSection}`; }
    if (ovLevel === "student") { if (!ovStudentId) return toast.error("Pick a student"); key = ovStudentId; label = ovStudentName; }
    setAddingOv(true);
    try {
      await api.post("/ai-tutor/overrides", { level: ovLevel, key, mode: ovMode });
      toast.success(`Override added: ${label} → ${MODE_LABELS[ovMode]}`);
      setOvStudentId(""); setOvStudentName("");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add override");
    } finally { setAddingOv(false); }
  };

  const removeOverride = async (o) => {
    try {
      await api.post("/ai-tutor/overrides", { level: o.level, key: o.key, mode: "INHERIT" });
      toast.success("Override removed");
      await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };

  const ovDisplay = (o) => {
    if (o.level === "class") return `Class ${o.key}`;
    if (o.level === "section") return `Class ${o.key.replace(":", "-")}`;
    return `Student ${o.key}`;
  };

  if (loading || !cfg) {
    return <Layout><div className="py-24 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="devadmin-ai-tutor-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <BrainCircuit className="h-7 w-7 text-violet-500" /> AI Tutor Mode Control
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Control how the AI Tutor answers — cost vs. quality — globally and per class / section / student.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-tutor-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save settings
          </Button>
        </div>

        {/* Global mode + knobs */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-5" data-testid="ai-tutor-global-card">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Global (School) mode</label>
            <Select value={cfg.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="rounded-xl h-11" data-testid="ai-tutor-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {modes.map((m) => <SelectItem key={m} value={m} data-testid={`ai-tutor-mode-${m}`}>{MODE_LABELS[m] || m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2 block">
                Cache similarity: {Number(cfg.similarity_threshold).toFixed(2)}
              </label>
              <Slider min={0.5} max={0.99} step={0.01} value={[Number(cfg.similarity_threshold)]}
                onValueChange={(v) => set("similarity_threshold", v[0])} data-testid="ai-tutor-threshold" />
              <p className="text-[11px] text-slate-400 mt-1">Higher = stricter cache match.</p>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Daily question limit / student</label>
              <Input type="number" min={0} value={cfg.daily_limit}
                onChange={(e) => set("daily_limit", Math.max(0, parseInt(e.target.value || "0", 10)))}
                className="rounded-xl" data-testid="ai-tutor-daily-limit" />
              <p className="text-[11px] text-slate-400 mt-1">0 = unlimited.</p>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Cache scope</label>
              <Select value={cfg.cache_scope} onValueChange={(v) => set("cache_scope", v)}>
                <SelectTrigger className="rounded-xl h-11" data-testid="ai-tutor-cache-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lesson">Per-lesson (recommended)</SelectItem>
                  <SelectItem value="school">School-wide (per subject)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 pt-2 border-t border-slate-100 dark:border-slate-700">
            {TOGGLES.map(([key, label, help]) => (
              <div key={key} className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
                  <div className="text-[11px] text-slate-400">{help}</div>
                </div>
                <Switch checked={!!cfg[key]} onCheckedChange={(v) => set(key, v)} data-testid={`ai-tutor-toggle-${key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Overrides */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4" data-testid="ai-tutor-overrides-card">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="h-4 w-4 text-violet-500" /> Mode overrides
          </h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 -mt-2">Most specific wins: <b>Student → Section → Class → Global</b>.</p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Level</label>
              <Select value={ovLevel} onValueChange={(v) => { setOvLevel(v); setOvStudentId(""); }}>
                <SelectTrigger className="rounded-xl h-10" data-testid="ov-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class">Class</SelectItem>
                  <SelectItem value="section">Section</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Class</label>
              <Select value={ovClass} onValueChange={setOvClass}>
                <SelectTrigger className="rounded-xl h-10" data-testid="ov-class"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>{(classMeta.classes || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(ovLevel === "section" || ovLevel === "student") && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Section</label>
                <Select value={ovSection} onValueChange={setOvSection}>
                  <SelectTrigger className="rounded-xl h-10" data-testid="ov-section"><SelectValue placeholder="Section" /></SelectTrigger>
                  <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {ovLevel === "student" && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Student</label>
                <Select value={ovStudentId} onValueChange={(v) => { setOvStudentId(v); setOvStudentName((students.find((s) => s.id === v) || {}).name || ""); }}>
                  <SelectTrigger className="rounded-xl h-10" data-testid="ov-student"><SelectValue placeholder="Student" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.roll_number})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Mode</label>
              <Select value={ovMode} onValueChange={setOvMode}>
                <SelectTrigger className="rounded-xl h-10" data-testid="ov-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{modes.map((m) => <SelectItem key={m} value={m}>{MODE_LABELS[m] || m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={addOverride} disabled={addingOv} variant="outline" className="rounded-full" data-testid="ov-add-btn">
            {addingOv ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Add override
          </Button>

          <div className="divide-y divide-slate-100 dark:divide-slate-700 border-t border-slate-100 dark:border-slate-700">
            {overrides.length === 0 && <p className="text-sm text-slate-400 py-3">No overrides — every class uses the global mode.</p>}
            {overrides.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-2.5" data-testid={`ov-row-${o.id}`}>
                <div>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{ovDisplay(o)}</span>
                  <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">{MODE_LABELS[o.mode] || o.mode}</span>
                </div>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => removeOverride(o)} data-testid={`ov-remove-${o.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
